import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../contexts/config-context';
import { youtubeId } from '../youtube';

const MOSAIC_SIZE = 5;
// how far, in pixels, the photo has to be dragged to turn to the next one
const SWIPE_DISTANCE = 50;

const Arrow = ({ dir, size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d={dir < 0 ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
    </svg>
);

const PlayBadge = () => (
    <span className='hs-gal-play'>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5-11-6.5Z" /></svg>
    </span>
);

export const Gallery = ({ listing }) => {
    const { api } = useContext(ConfigContext);
    const { t } = useTranslation(["details"]);
    // index of the item shown full screen, or null when the viewer is closed
    const [open, setOpen] = useState(null);

    const media = useMemo(() => {
        const base = `${api.host}:${api.port}`;
        const photos = (listing.photos || []).map(p => {
            const sources = p.photo.ratios.null.sources['image/webp'];
            const largest = Math.max(...Object.keys(sources).map(Number));
            return { type: 'photo', thumb: `${base}${sources['500'] || sources[largest]}`, src: `${base}${sources[largest]}` };
        });
        const videos = (listing.videos || []).map(v => ({ type: 'video', thumb: `${base}${v.thumbnail}`, src: `${base}${v.video}` }));
        const youtube = youtubeId(listing.video_url);
        const linked = youtube
            ? [{ type: 'youtube', thumb: `https://img.youtube.com/vi/${youtube}/hqdefault.jpg`, src: `https://www.youtube.com/embed/${youtube}?autoplay=1` }]
            : [];
        return [...photos, ...videos, ...linked];
    }, [listing.photos, listing.videos, listing.video_url, api]);

    const total = media.length;

    // dragging the stage sideways, with a mouse or a finger, turns to the next or previous item
    const [drag, setDrag] = useState(0);
    const dragStart = useRef(null);
    const startDrag = (e) => {
        if (total < 2 || e.button > 0 || e.target.closest('button, video, iframe')) return;
        dragStart.current = e.clientX;
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const moveDrag = (e) => {
        if (dragStart.current !== null) setDrag(e.clientX - dragStart.current);
    };
    const endDrag = () => {
        if (dragStart.current === null) return;
        dragStart.current = null;
        if (Math.abs(drag) >= SWIPE_DISTANCE) setOpen(i => (i + (drag < 0 ? 1 : -1) + total) % total);
        setDrag(0);
    };

    const isOpen = open !== null;

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(null);
            if (e.key === 'ArrowRight') setOpen(i => (i + 1) % total);
            if (e.key === 'ArrowLeft') setOpen(i => (i - 1 + total) % total);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, total]);

    if (total === 0) {
        return <div className='hs-gal hs-gal-empty' style={{ backgroundImage: `url(${api.host}:${api.port}/media/No_Image_Available.jpg)` }}></div>;
    }

    const shown = media.slice(0, MOSAIC_SIZE);
    const current = open === null ? null : media[open];

    return (
        <>
            <div className={`hs-gal count-${shown.length}`}>
                {shown.map((item, i) => (
                    <button key={item.src} className='hs-gal-tile' style={{ backgroundImage: `url(${i === 0 && item.type === 'photo' ? item.src : item.thumb})` }}
                        onClick={() => setOpen(i)} aria-label={t('open_gallery')}>
                        {item.type !== 'photo' && <PlayBadge />}
                        {i === MOSAIC_SIZE - 1 && total > MOSAIC_SIZE &&
                            <span className='hs-gal-more'>{t('more_photos', { n: total - MOSAIC_SIZE })}</span>}
                    </button>
                ))}
                <button className='hs-gal-all' onClick={() => setOpen(0)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                    {t('all_photos', { n: total })}
                </button>
            </div>

            {current &&
                <div className='hs hs-lightbox'>
                    <div className='hs-lightbox-head'>
                        <span className='hs-lightbox-index'>{open + 1} / {total}</span>
                        <span className='hs-lightbox-title'>{listing.title}</span>
                        <button className='hs-lightbox-close' onClick={() => setOpen(null)} aria-label={t('close')}>×</button>
                    </div>
                    <div className={`hs-lightbox-stage ${total > 1 ? 'swipe' : ''} ${drag ? 'dragging' : ''}`}
                        onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
                        {current.type === 'youtube'
                            ? <iframe key={current.src} src={current.src} title={listing.title} allow='autoplay; encrypted-media; picture-in-picture' allowFullScreen />
                            : current.type === 'video'
                                ? <video key={current.src} src={current.src} poster={current.thumb} controls autoPlay />
                                : <img key={current.src} src={current.src} alt={listing.title} draggable={false}
                                    style={drag ? { transform: `translateX(${drag}px)` } : undefined} />}
                        {total > 1 && <>
                            <button className='hs-lightbox-arrow prev' onClick={() => setOpen((open - 1 + total) % total)} aria-label={t('prev')}><Arrow dir={-1} size={18} /></button>
                            <button className='hs-lightbox-arrow next' onClick={() => setOpen((open + 1) % total)} aria-label={t('next')}><Arrow dir={1} size={18} /></button>
                        </>}
                    </div>
                    <div className='hs-lightbox-strip'>
                        {media.map((item, i) => (
                            <button key={item.src} className={i === open ? 'on' : ''} style={{ backgroundImage: `url(${item.thumb})` }}
                                onClick={() => setOpen(i)} aria-label={`${i + 1}`}></button>
                        ))}
                    </div>
                </div>}
        </>
    );
};
